import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FileDropzone,
  FileUploadItem,
  LoadingButton,
  Typography,
} from '@tempmee/react';
import { useUploadMembershipsCsv } from '../../../../domain/Awards';
import { uploadErrorMessage } from './awardsErrors';
import { CsvUploadResults } from './CsvUploadResults';

/**
 * Bulk membership upsert from a CSV of (id, status) rows.
 *
 * The dialog deliberately stays open after a successful upload: the endpoint
 * applies what it can and reports the rest, so partial success is the normal
 * outcome and the admin needs to see which rows failed and why before closing.
 */
export const UploadUsersDialog = ({
  open, onClose, cycleId, cycleName,
}) => {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const reset = () => {
    setFile(null);
    setResult(null);
    setError(null);
  };

  useEffect(() => {
    if (open) reset();
  }, [open]);

  const upload = useUploadMembershipsCsv(cycleId, {
    onSuccess: (uploadResult) => {
      setResult(uploadResult);
      setError(null);
    },
    onError: (uploadError) => setError(uploadErrorMessage(uploadError)),
  });

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle closeLabel="Dismiss dialog">Upload Users to Cycle</DialogTitle>

      <DialogContent>
        <Typography variant="body1" bold>Cycle Name:</Typography>
        <Typography variant="body1">{cycleName}</Typography>

        {result ? (
          <CsvUploadResults result={result} />
        ) : (
          <>
            {/* The field name rides on `browseLabel`, not `label`: FileDropzone
                renders `label` as a detached <span>, while its real
                <label htmlFor> wraps the input and contains `browseLabel`. So
                only `browseLabel` becomes the input's accessible name — this
                keeps the visible prompt and that name the same string. */}
            <FileDropzone
              id="membership-csv"
              browseLabel="Upload User List (.csv)"
              accept=".csv"
              hint="Two columns: id and status."
              error={Boolean(error)}
              errorText={error}
              onSelectFiles={(files) => setFile(files[0] ?? null)}
            />

            {file ? (
              // A successful upload swaps this whole branch for CsvUploadResults,
              // so `complete` is never the right state here — the file is either
              // uploading, failed, or merely selected. FileUploadItem has no
              // "selected" status, so present that as a ready-to-upload row (empty
              // bar, explicit label) rather than letting it fall through to
              // `complete`, which renders a 100% green "Upload Complete" bar and
              // reads as a finished import before any request has been sent.
              <FileUploadItem
                name={file.name}
                status={error ? 'failed' : 'uploading'}
                value={upload.isPending || error ? undefined : 0}
                statusLabel={
                  upload.isPending || error ? undefined : 'Ready to upload'
                }
              />
            ) : null}
          </>
        )}
      </DialogContent>

      <DialogActions>
        {result ? (
          <>
            <Button variant="outlined" onClick={reset}>
              Upload another
            </Button>
            <Button onClick={onClose}>Done</Button>
          </>
        ) : (
          <LoadingButton
            loading={upload.isPending}
            disabled={!file}
            onClick={() => upload.mutate(file)}
          >
            Upload
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

UploadUsersDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  cycleId: PropTypes.string.isRequired,
  cycleName: PropTypes.string.isRequired,
};
